import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BulkProductImportSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importHistory, setImportHistory] = useState([]);

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      loadCategories();
      loadImportHistory();
    }
  }, [currentTenant, isGymOwner, isStaff]);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        ?.from('pos_categories')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Load categories error:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      const { data, error } = await supabase
        ?.from('pos_inventory_logs')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('reason', 'bulk_import')
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setImportHistory(data || []);
    } catch (error) {
      console.error('Load import history error:', error);
    }
  };

  // Generate Excel template
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'SKU*': 'PROD-001',
        'Product Name*': 'Sample Product',
        'Description': 'Product description here',
        'Category': categories?.[0]?.name || 'Supplements',
        'Price*': 29.99,
        'Cost Price': 15.00,
        'Stock Quantity': 100,
        'Low Stock Threshold': 10,
        'Barcode': '1234567890123',
        'Track Inventory': 'YES',
        'Status': 'active',
        'Image URL': 'https://example.com/image.jpg'
      },
      {
        'SKU*': 'PROD-002',
        'Product Name*': 'Another Product',
        'Description': 'Another description',
        'Category': categories?.[1]?.name || 'Apparel',
        'Price*': 49.99,
        'Cost Price': 25.00,
        'Stock Quantity': 50,
        'Low Stock Threshold': 5,
        'Barcode': '',
        'Track Inventory': 'YES',
        'Status': 'active',
        'Image URL': ''
      }
    ];

    const wb = XLSX?.utils?.book_new();
    const ws = XLSX?.utils?.json_to_sheet(templateData);

    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 40 }, { wch: 15 },
      { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 20 },
      { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 30 }
    ];

    XLSX?.utils?.book_append_sheet(wb, ws, 'Products');
    const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    saveAs(blob, `product-import-template-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      validateFile(selectedFile);
    }
  };

  // Validate file
  const validateFile = (selectedFile) => {
    const errors = [];
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes?.includes(selectedFile?.type)) {
      errors?.push('Invalid file type. Please upload an Excel file (.xlsx or .xls)');
    }

    if (selectedFile?.size > 5 * 1024 * 1024) {
      errors?.push('File size exceeds 5MB limit');
    }

    if (errors?.length > 0) {
      setValidationErrors(errors);
      setFile(null);
    } else {
      setValidationErrors([]);
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      validateFile(e?.dataTransfer?.files?.[0]);
    }
  };

  // Parse Excel file
  const parseExcelFile = async (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e?.target?.result);
        const workbook = XLSX?.read(data, { type: 'array' });
        const sheetName = workbook?.SheetNames?.[0];
        const worksheet = workbook?.Sheets?.[sheetName];
        const jsonData = XLSX?.utils?.sheet_to_json(worksheet);
        
        setParsedData(jsonData);
        setShowPreview(true);
        validateProductData(jsonData);
      } catch (error) {
        setValidationErrors(['Failed to parse Excel file. Please check the format.']);
      }
    };
    
    reader?.readAsArrayBuffer(file);
  };

  // Validate product data
  const validateProductData = (products) => {
    const errors = [];
    const validStatuses = ['active', 'inactive', 'out_of_stock', 'discontinued'];
    
    products?.forEach((product, index) => {
      const rowNum = index + 2;
      
      if (!product?.['SKU*']) {
        errors?.push(`Row ${rowNum}: SKU is required`);
      }
      if (!product?.['Product Name*']) {
        errors?.push(`Row ${rowNum}: Product Name is required`);
      }
      if (!product?.['Price*'] || isNaN(parseFloat(product?.['Price*']))) {
        errors?.push(`Row ${rowNum}: Valid Price is required`);
      }
      
      if (product?.['Status'] && !validStatuses?.includes(product?.['Status']?.toLowerCase())) {
        errors?.push(`Row ${rowNum}: Invalid status`);
      }
    });
    
    setValidationErrors(errors);
  };

  // Handle import
  const handleImport = async () => {
    if (!file || parsedData?.length === 0) return;
    
    if (validationErrors?.length > 0) {
      alert('Please fix validation errors before importing');
      return;
    }
    
    setImporting(true);
    
    try {
      const categoryMap = {};
      categories?.forEach(cat => {
        categoryMap[cat?.name?.toLowerCase()] = cat?.id;
      });
      
      const productsToInsert = parsedData?.map(product => {
        const categoryName = product?.['Category']?.toLowerCase();
        const categoryId = categoryName ? categoryMap?.[categoryName] : null;
        
        return {
          tenant_id: currentTenant?.id,
          sku: product?.['SKU*']?.toString()?.trim(),
          name: product?.['Product Name*']?.toString()?.trim(),
          description: product?.['Description']?.toString()?.trim() || null,
          category_id: categoryId,
          price: parseFloat(product?.['Price*']) || 0,
          cost: parseFloat(product?.['Cost Price']) || 0,
          stock_quantity: parseInt(product?.['Stock Quantity']) || 0,
          low_stock_threshold: parseInt(product?.['Low Stock Threshold']) || 10,
          barcode: product?.['Barcode']?.toString()?.trim() || null,
          track_inventory: product?.['Track Inventory']?.toString()?.toUpperCase() === 'YES',
          is_active: product?.['Status']?.toLowerCase() === 'active' || !product?.['Status'],
          image_url: product?.['Image URL']?.toString()?.trim() || null,
          created_by: user?.id
        };
      });
      
      const { data, error } = await supabase
        ?.from('pos_products')
        ?.insert(productsToInsert)
        ?.select();
      
      if (error) throw error;
      
      setImportResults({
        success: true,
        imported: data?.length || 0,
        failed: 0,
        timestamp: new Date()?.toISOString()
      });
      
      // Reset form
      setFile(null);
      setParsedData([]);
      setShowPreview(false);
      loadImportHistory();
      
    } catch (error) {
      console.error('Import error:', error);
      setImportResults({
        success: false,
        imported: 0,
        failed: parsedData?.length,
        error: error?.message
      });
    } finally {
      setImporting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Bulk Product Import</h1>
            <Button
              variant="outline"
              onClick={() => navigate('/inventory-management-dashboard')}
            >
              <Icon name="ArrowLeft" size={18} className="mr-2" />
              Back to Inventory
            </Button>
          </div>
          <p className="text-muted-foreground">
            Import multiple products using Excel spreadsheet templates
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Import Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Download Template */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 rounded-full p-3">
                  <Icon name="Download" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-foreground mb-2">Step 1: Download Template</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Download the pre-formatted Excel template with required fields and sample data
                  </p>
                  <Button onClick={handleDownloadTemplate}>
                    <Icon name="FileSpreadsheet" size={18} className="mr-2" />
                    Download Excel Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2: Upload File */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-primary/10 rounded-full p-3">
                  <Icon name="Upload" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-foreground mb-2">Step 2: Upload File</h2>
                  <p className="text-sm text-muted-foreground">
                    Upload your completed Excel file with product data
                  </p>
                </div>
              </div>

              {/* Drag and Drop Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">
                  Drag and drop your Excel file here
                </p>
                <p className="text-sm text-muted-foreground mb-4">or</p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button as="span">
                    <Icon name="FolderOpen" size={18} className="mr-2" />
                    Browse Files
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-4">
                  Supported formats: .xlsx, .xls (Max 5MB)
                </p>
              </div>

              {/* File Info */}
              {file && (
                <div className="mt-4 bg-primary/10 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="FileSpreadsheet" size={24} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{file?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file?.size / 1024)?.toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setParsedData([]);
                      setShowPreview(false);
                      setValidationErrors([]);
                    }}
                    className="text-danger hover:bg-danger/10 p-2 rounded transition-smooth"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>
              )}

              {/* Validation Errors */}
              {validationErrors?.length > 0 && (
                <div className="mt-4 bg-danger/10 rounded-lg p-4 border border-danger/20">
                  <h3 className="text-sm font-semibold text-danger mb-2 flex items-center">
                    <Icon name="AlertCircle" size={16} className="mr-2" />
                    Validation Errors ({validationErrors?.length})
                  </h3>
                  <ul className="space-y-1">
                    {validationErrors?.slice(0, 10)?.map((error, index) => (
                      <li key={index} className="text-xs text-foreground">
                        • {error}
                      </li>
                    ))}
                  </ul>
                  {validationErrors?.length > 10 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      +{validationErrors?.length - 10} more errors
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: Preview and Import */}
            {showPreview && parsedData?.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <Icon name="Eye" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-foreground mb-2">Step 3: Preview & Import</h2>
                    <p className="text-sm text-muted-foreground">
                      Review parsed data before importing ({parsedData?.length} products)
                    </p>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-2 text-muted-foreground font-medium">SKU</th>
                        <th className="text-left p-2 text-muted-foreground font-medium">Name</th>
                        <th className="text-left p-2 text-muted-foreground font-medium">Category</th>
                        <th className="text-left p-2 text-muted-foreground font-medium">Price</th>
                        <th className="text-left p-2 text-muted-foreground font-medium">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData?.slice(0, 5)?.map((product, index) => (
                        <tr key={index} className="border-b border-border">
                          <td className="p-2 text-foreground">{product?.['SKU*']}</td>
                          <td className="p-2 text-foreground">{product?.['Product Name*']}</td>
                          <td className="p-2 text-foreground">{product?.['Category']}</td>
                          <td className="p-2 text-foreground">${product?.['Price*']}</td>
                          <td className="p-2 text-foreground">{product?.['Stock Quantity']}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedData?.length > 5 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      +{parsedData?.length - 5} more products
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleImport}
                  disabled={importing || validationErrors?.length > 0}
                  className="w-full"
                >
                  {importing ? (
                    <>
                      <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Icon name="Upload" size={18} className="mr-2" />
                      Import {parsedData?.length} Products
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Import Results */}
            {importResults && (
              <div className={`rounded-lg p-6 border ${
                importResults?.success 
                  ? 'bg-success/10 border-success/20' :'bg-danger/10 border-danger/20'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon 
                    name={importResults?.success ? 'CheckCircle' : 'XCircle'} 
                    size={24} 
                    className={importResults?.success ? 'text-success' : 'text-danger'} 
                  />
                  <h3 className="text-lg font-bold text-foreground">
                    {importResults?.success ? 'Import Successful!' : 'Import Failed'}
                  </h3>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Imported:</span> {importResults?.imported} products
                  </p>
                  {importResults?.failed > 0 && (
                    <p className="text-sm text-foreground">
                      <span className="font-medium">Failed:</span> {importResults?.failed} products
                    </p>
                  )}
                  {importResults?.error && (
                    <p className="text-sm text-danger">
                      <span className="font-medium">Error:</span> {importResults?.error}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Import History Panel */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-4 sticky top-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
                <Icon name="History" size={20} className="mr-2" />
                Import History
              </h2>

              {importHistory?.length > 0 ? (
                <div className="space-y-3">
                  {importHistory?.map((log) => (
                    <div key={log?.id} className="bg-background rounded p-3 border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-primary">
                          {new Date(log?.created_at)?.toLocaleDateString()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log?.created_at)?.toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">
                        Quantity: {log?.quantity_change > 0 ? '+' : ''}{log?.quantity_change}
                      </p>
                      {log?.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{log?.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No import history</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default BulkProductImportSystem;